import type { MeResponse } from "@stb/api-contract"
import { PRODUCT_CONFIG } from "@stb/product-config"
import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"

import { getDeletionStatus, requestAccountDeletion, restoreAccount } from "@/account/account.server"
import { getSessionUser } from "@/auth/session.server"
import { getUserById, setDisplayName } from "@/auth/user.server"
import {
  getBillingHoldings,
  hasLiveStripeSubscription,
  hasStripeCustomer,
  listLiveStoreSubscriptions,
  type BillingHoldings,
} from "@/billing/billing-state.server"
import { getOrganisationPlan } from "@/billing/organisation-entitlements.server"
import { getStripeConfig } from "@/billing/stripe-config.server"
import type { PriceKey } from "@/billing/stripe.server"
import type { StoreSubscriptionView } from "@/billing/types"
import { isAppError } from "@/core/app-error"
import { logger } from "@/core/logger.server"
import { getMainDb } from "@/db/main/db.server"
import { sendAccountDeletionScheduledEmail } from "@/email/templates.server"
import { parseFormData } from "@/form"
import type { FormError } from "@/form/use-form-action"
import { getOrCreateUserOrganisation } from "@/organisations/organisations.server"
import { listUserOrganisations } from "@/trips/me-organisations.server"

const ADD_ONS = ["receipts", "invoicing", "vehicle"] as const

export interface AccountPageData {
  email: string
  /** Saved display name; null until the user sets one (the header then shows the email's local part). */
  name: string | null
  plan: MeResponse["plan"]
  /** `checkoutOpen`: Stripe is configured, so Upgrade can start a checkout. `canManage`: the portal can open. `hasSubscription`: a live Stripe subscription, so a new checkout would be refused. */
  billing: {
    checkoutOpen: boolean
    canManage: boolean
    hasSubscription: boolean
    /** What the live subscriptions hold; drives change-in-place instead of a second checkout (T589). */
    held: BillingHoldings
    /** Sold plans with both intervals priced. */
    plans: ("logbook" | "pro")[]
    /** Single add-ons with their prices configured. */
    addOns: ("receipts" | "invoicing" | "vehicle")[]
    /** Live Apple / Google subscriptions (T1163); read whatever STORE_BILLING_ENABLED says. */
    store: StoreSubscriptionView[]
    /** Server time (ISO): the page decides "period over" with it, so server render and browser agree. */
    now: string
  }
  /** Teams the user owns, soft-deleted ones included: the Owner's export still answers (T1246). */
  ownedTeams: { id: string; name: string }[]
  /** ISO timestamps, set while the account is scheduled for deletion. */
  deletion: { requestedAt: string; purgeAfter: string } | null
}

export const deleteAccountFormSchema = z.object({
  confirm: z.string().min(1, "Type your account email to confirm."),
  // a checked Radix checkbox submits "on"; unchecked submits nothing
  understood: z.literal("on", { error: "Tick the box to confirm you understand." }),
})

async function requireSessionUser() {
  const user = await getSessionUser()
  if (!user) throw new Error("Sign in to continue.")
  return user
}

/**
 * What the account page may sell, from the configured Stripe prices: a plan or feature add-on needs
 * both intervals (the card offers a monthly/annual choice); the vehicle add-on is monthly only.
 * No price means no button, so a half-configured environment shows no dead button.
 */
function billingOffers(): Pick<AccountPageData["billing"], "checkoutOpen" | "plans" | "addOns"> {
  const config = getStripeConfig()
  const priced = (name: string, intervals: readonly ("MONTH" | "YEAR")[]) =>
    intervals.every((i) => config?.prices[`${name.toUpperCase()}_${i}` as PriceKey] !== undefined)
  const both = ["MONTH", "YEAR"] as const
  const plans = PRODUCT_CONFIG.webCheckoutPlans.filter((plan) => priced(plan, both))
  const addOns = ADD_ONS.filter((id) => priced(id, id === "vehicle" ? ["MONTH"] : both))
  return { checkoutOpen: plans.length + addOns.length > 0, plans, addOns }
}

/** Email and deletion state of the signed-in user; the route guard has already required a session. */
export const getAccountPageData = createServerFn({ method: "GET" }).handler(
  async (): Promise<AccountPageData> => {
    const user = await requireSessionUser()
    const db = getMainDb()
    const organisationId = await getOrCreateUserOrganisation(db, user.id)
    const [status, plan, canManage, hasSubscription, held, storeRows, memberships, profile] =
      await Promise.all([
        getDeletionStatus(db, user.id),
        getOrganisationPlan(db, organisationId),
        hasStripeCustomer(db, organisationId),
        hasLiveStripeSubscription(db, organisationId),
        getBillingHoldings(db, organisationId),
        listLiveStoreSubscriptions(db, organisationId),
        listUserOrganisations(db, user.id),
        getUserById(user.id),
      ])
    // Reading is not gated by STORE_BILLING_ENABLED: after a rollback a paying store customer still
    // sees the subscription and gets no web checkout the server would refuse (iap-architecture.md 8).
    const store: StoreSubscriptionView[] = storeRows.map((row) => ({
      ...row,
      currentPeriodEnd: row.currentPeriodEnd?.toISOString() ?? null,
    }))
    return {
      email: user.email,
      name: profile?.displayName ?? null,
      plan,
      billing: {
        ...billingOffers(),
        canManage,
        hasSubscription,
        held,
        store,
        now: new Date().toISOString(),
      },
      ownedTeams: memberships
        .filter((m) => m.kind === "team" && m.role === "owner" && m.status === "active")
        .map((m) => ({ id: m.id, name: m.name })),
      deletion: status
        ? {
            requestedAt: status.requestedAt.toISOString(),
            purgeAfter: status.purgeAfter.toISOString(),
          }
        : null,
    }
  },
)

export const profileNameSchema = z.object({ name: z.string().trim().max(80) })

/** Saves the signed-in user's display name (empty clears it). */
export const updateProfileNameAction = createServerFn({ method: "POST" })
  .validator((data: unknown) => profileNameSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireSessionUser()
    await setDisplayName(user.id, data.name)
  })

/** Same behaviour as POST /api/v1/account/delete, for the signed-in web session. */
export const deleteAccountFormAction = createServerFn({ method: "POST" })
  .validator((data: FormData) => parseFormData(deleteAccountFormSchema, data))
  .handler(async ({ data }) => {
    const user = await requireSessionUser()
    try {
      const status = await requestAccountDeletion(getMainDb(), user, data.confirm, {
        sendScheduledEmail: sendAccountDeletionScheduledEmail,
      })
      return { purgeAfter: status.purgeAfter.toISOString() }
    } catch (error) {
      if (isAppError(error) && error.code === "VALIDATION_FAILED") {
        throw {
          message: "The email does not match your account email.",
          fields: { confirm: ["The email does not match your account email."] },
        } satisfies FormError<typeof deleteAccountFormSchema>
      }
      if (isAppError(error)) throw new Error(error.message, { cause: error })
      logger.error("account.deletion.web_failed", { user_id: user.id })
      throw new Error("Something went wrong. Please try again.", { cause: error })
    }
  })

/** Same behaviour as POST /api/v1/account/restore. */
export const restoreAccountAction = createServerFn({ method: "POST" }).handler(async () => {
  const user = await requireSessionUser()
  try {
    await restoreAccount(getMainDb(), user.id)
  } catch (error) {
    if (isAppError(error)) throw new Error(error.message, { cause: error })
    logger.error("account.restore.web_failed", { user_id: user.id })
    throw new Error("Something went wrong. Please try again.", { cause: error })
  }
})
